import { useViewPort } from "@website/hooks/viewport";
import { MaterialIcon } from "@website/icons/material";
import styles from "@website/ui/auto-complete/auto-complete.module.scss";
import { AutoCompletePropertiesType } from "@website/ui/auto-complete/auto-complete.types";
import { useEnd, useStart, useSwap } from "@website/ui/auto-complete/hooks";
import { Chips } from "@website/ui/chips";
import { ListItem } from "@website/ui/list-item";
import { Loading } from "@website/ui/loading";
import { PopoverFull } from "@website/ui/popover-full";
import { SearchField } from "@website/ui/search-field";
import { TextField } from "@website/ui/text-field";
import { TopAppBar } from "@website/ui/top-app-bar";
import { SizeType } from "@website/ui/types";
import { FC, memo } from "react";

const AutoComplete: FC<AutoCompletePropertiesType> = (properties) => {
  const {
    className,
    inputClassName,
    parts,
    error,
    fullWidth,
    onSwap,
    hideBorder,
    hasDropdownIcon,
    labelClassName,
    hideSearchBar,
    hideHeader,
    disabled
  } = properties;
  const { isMobile } = useViewPort();
  const {
    startTextFieldProperties,
    startItemsProperties,
    startPopoverProperties,
    startPopoverSearchFieldProperties,
    startHistoryItemsProperties,
    startTopAppBarProperties
  } = useStart(properties);
  const {
    endTextFieldProperties,
    endItemsProperties,
    endPopoverProperties,
    endPopoverSearchFieldProperties,
    endHistoryItemsProperties,
    endTopAppBarProperties,
    endtags
  } = useEnd(properties);
  const { swapButtonProperties } = useSwap(properties);

  return (
    <div
      className={`${styles["auto-complete"]} ${
        fullWidth ? styles["full-width"] : ""
      } ${className ?? ""}`}
    >
      <div className={styles["box"]}>
        <div className={styles["inputs-container"]}>
          <div className={styles["start"]}>
            <TextField
              {...startTextFieldProperties}
              EndIcon={
                hasDropdownIcon && (
                  <MaterialIcon name="arrow_drop_down" variant="fill" />
                )
              }
              className={`styles["input"] ${hideBorder && "border-none"}`}
              disabled={disabled}
              fullWidth
              inputClassName={inputClassName}
              labelClassName={labelClassName}
            />
          </div>
          {parts[1] ? (
            <>
              <div className={styles["middle"]}>
                <div className={styles["middle-line"]} />
              </div>
              {onSwap && (
                <button
                  {...swapButtonProperties}
                  aria-label="swap selected items"
                  className={styles["swap-button"]}
                >
                  <MaterialIcon
                    className={styles["swap-button-icon"]}
                    name="swap_horiz"
                    variant="outlined"
                  />
                </button>
              )}
              <div className={styles["end"]}>
                <TextField
                  {...endTextFieldProperties}
                  className={`${styles["input"]} ${
                    hideBorder && "border-none"
                  }`}
                  fullWidth
                  inputClassName={inputClassName}
                />
              </div>
            </>
          ) : null}
        </div>
        {error ? <div className={styles["error"]}>{error}</div> : null}
      </div>
      <PopoverFull {...startPopoverProperties}>
        <div className={styles["popover"]}>
          {!hideHeader && <TopAppBar {...startTopAppBarProperties} />}
          {!hideSearchBar && (
            <div className={styles["popover-header"]}>
              <SearchField
                {...startPopoverSearchFieldProperties}
                color={isMobile ? "white" : "transparent"}
              />
            </div>
          )}
          <div className={styles["popover-list"]}>
            <div className={styles["popover-list-divider"]} />
            {parts[0]?.loading ? (
              <div className={styles["loading"]}>
                <Loading size={SizeType.lg} />
                <div>در حال جستجو هستیم</div>
              </div>
            ) : null}
            {parts[0]?.NoResult ?? null}
            {!parts[0]?.query &&
            startHistoryItemsProperties &&
            startHistoryItemsProperties?.length > 0 ? (
              <>
                <div className={styles["popover-history-title"]}>
                  {parts[0]?.historyTitle}
                </div>
                {startHistoryItemsProperties.map((item) => (
                  <ListItem key={item.id} {...item} />
                ))}
                <div className={styles["popover-history-bottom"]} />
              </>
            ) : null}
            {startItemsProperties && startItemsProperties?.length > 0 ? (
              <>
                {parts[0]?.listTitle ? (
                  <div className={styles["popover-list-title"]}>
                    {parts[0]?.listTitle}
                  </div>
                ) : null}

                {startItemsProperties.map((item) => (
                  <ListItem key={item.id} {...item} />
                ))}
              </>
            ) : null}
          </div>
        </div>
      </PopoverFull>
      {parts[1] ? (
        <PopoverFull {...endPopoverProperties}>
          <div className={styles["popover"]}>
            <TopAppBar {...endTopAppBarProperties} />
            <div className={styles["popover-header"]}>
              <SearchField
                {...endPopoverSearchFieldProperties}
                color={isMobile ? "white" : "transparent"}
              />
            </div>
            <div className={styles["popover-list"]}>
              <div className={styles["popover-list-divider"]} />
              {parts[1]?.loading ? (
                <div className={styles["loading"]}>
                  <Loading size={SizeType.lg} />
                  <div>در حال جستجو هستیم</div>
                </div>
              ) : null}
              {parts[1]?.NoResult ?? null}
              {!parts[1]?.query &&
              endHistoryItemsProperties &&
              endHistoryItemsProperties?.length > 0 ? (
                <>
                  <div className={styles["popover-history-title"]}>
                    {parts[1]?.historyTitle}
                  </div>
                  {endHistoryItemsProperties.map((item) => (
                    <ListItem key={item.id} {...item} />
                  ))}
                  <div className={styles["popover-history-bottom"]} />
                </>
              ) : null}
              {endItemsProperties && endItemsProperties?.length > 0 ? (
                <>
                  {parts[1]?.listTitle ? (
                    <div className={styles["popover-list-title"]}>
                      {parts[1]?.listTitle}
                    </div>
                  ) : null}
                  {endItemsProperties.map((item) => (
                    <ListItem key={item.id} {...item} />
                  ))}
                </>
              ) : null}
              {endItemsProperties?.length === 0 && (
                <div className="border-outline-thin flex flex-wrap gap-2 border-t px-6 py-2">
                  {endtags.map((item) => (
                    <Chips
                      {...item}
                      className="cursor-pointer"
                      color="primary"
                      key={item.id}
                      text={item.title}
                      variant="outline"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </PopoverFull>
      ) : null}
    </div>
  );
};

const AutoCompleteMemoized = memo(AutoComplete);
export { AutoCompleteMemoized as AutoComplete };
